import { computed } from 'vue'
import { useAuthStore } from '@/modules/auth'
import type {
  TPurchaseOrderNumberingMode,
  TPurchaseOrderNumberingSettings,
  TPurchaseOrderNumberingTypeSetting,
  TTenantSettings,
} from '@/core/types'

const defaultNumbering: TPurchaseOrderNumberingSettings = {
  normal: { mode: 'automatic', prefix: 'OC' },
  additive: { mode: 'automatic', prefix: 'OC-A' },
  deductive: { mode: 'automatic', prefix: 'OC-D' },
}

const defaultSettings: TTenantSettings = {
  locale: 'es',
  timezone: 'America/Mexico_City',
  date_format: 'dd/MM/yyyy',
  time_format: '24h',
  currency: {
    symbol: '$',
    decimal_places: 2,
    decimal_separator: '.',
    thousands_separator: ',',
  },
  modules: {
    purchase_orders: {
      has_lines: { default: true, visible: true },
      currency: { default: 'MXN', visible: true },
      shipping_section: { visible: true },
      notes: { visible: true },
      billing_metrics: { visible: false },
      open_assignment: { enabled: false },
      numbering: { ...defaultNumbering },
    },
    spend_projects: {
      exchange_rate: { default: 1, visible: true },
      supplier_type_keys: [],
    },
    gym: {
      daily_close: { enabled: false, emails: [] },
    },
    pos: {
      require_open_shift: true,
      blind_shift_close: false,
      require_supervisor_on_shift_close: false,
      shift_close_variance_threshold: 0,
      max_shift_hours: 12,
      shift_warning_hours: 10,
      block_sales_when_shift_overdue: true,
      allow_shift_extension: true,
      shift_extension_hours: 4,
      receipt_footer: '',
      receipt_width_mm: 80,
      numbering: { mode: 'automatic', prefix: 'TK' },
      cash_denominations: {
        bills: [1000, 500, 200, 100, 50, 20],
        coins: [20, 10, 5, 2, 1, 0.5],
      },
    },
  },
}

const coerceBool = (value: unknown, fallback: boolean): boolean => {
  if (value === true || value === 1 || value === '1') return true
  if (value === false || value === 0 || value === '0') return false
  return fallback
}

const coerceExchangeRate = (value: unknown, fallback: number): number => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const coerceNumberingMode = (
  value: unknown,
  fallback: TPurchaseOrderNumberingMode,
): TPurchaseOrderNumberingMode => {
  if (value === 'automatic' || value === 'manual') return value
  return fallback
}

const coercePrefix = (value: unknown, fallback: string): string => {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase()
  if (raw && raw.length <= 5 && /^[A-Z0-9\-]+$/.test(raw)) return raw
  return fallback
}

const normalizeDenominationList = (
  value: unknown,
  fallback: number[],
): number[] => {
  if (!Array.isArray(value)) return [...fallback]
  const parsed = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0)
    .map((item) => Math.round(item * 100) / 100)
  const unique = [...new Set(parsed)].sort((a, b) => b - a)
  return unique.length > 0 ? unique : [...fallback]
}

const coerceEmailList = (value: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(value)) return [...fallback]
  const emails = value
    .map((item) =>
      String(item ?? '')
        .trim()
        .toLowerCase(),
    )
    .filter((item) => item !== '')
  return [...new Set(emails)].slice(0, 10)
}

const coerceNumberingType = (
  value: Partial<TPurchaseOrderNumberingTypeSetting> | undefined,
  fallback: TPurchaseOrderNumberingTypeSetting,
): TPurchaseOrderNumberingTypeSetting => ({
  mode: coerceNumberingMode(value?.mode, fallback.mode),
  prefix: coercePrefix(value?.prefix, fallback.prefix),
})

export const useTenantSettings = () => {
  const auth = useAuthStore()

  const settings = computed<TTenantSettings>(() => {
    const fromAuth: Partial<TTenantSettings> = auth.user?.tenant?.settings ?? {}
    const po = fromAuth.modules?.purchase_orders
    const sp = fromAuth.modules?.spend_projects
    const gym = fromAuth.modules?.gym
    const defaultsPo = defaultSettings.modules!.purchase_orders!
    const defaultsSp = defaultSettings.modules!.spend_projects!
    const defaultsGym = defaultSettings.modules!.gym!

    return {
      ...defaultSettings,
      ...fromAuth,
      currency: {
        ...defaultSettings.currency,
        ...(fromAuth.currency ?? {}),
      },
      modules: {
        ...defaultSettings.modules,
        ...(fromAuth.modules ?? {}),
        purchase_orders: {
          ...defaultsPo,
          ...(po ?? {}),
          has_lines: {
            default: coerceBool(
              po?.has_lines?.default,
              defaultsPo.has_lines.default,
            ),
            visible: coerceBool(
              po?.has_lines?.visible,
              defaultsPo.has_lines.visible,
            ),
          },
          currency: {
            default: po?.currency?.default ?? defaultsPo.currency.default,
            visible: coerceBool(
              po?.currency?.visible,
              defaultsPo.currency.visible,
            ),
          },
          shipping_section: {
            visible: coerceBool(
              po?.shipping_section?.visible,
              defaultsPo.shipping_section.visible,
            ),
          },
          notes: {
            visible: coerceBool(po?.notes?.visible, defaultsPo.notes.visible),
          },
          billing_metrics: {
            visible: coerceBool(
              po?.billing_metrics?.visible,
              defaultsPo.billing_metrics.visible,
            ),
          },
          open_assignment: {
            enabled: coerceBool(
              po?.open_assignment?.enabled,
              defaultsPo.open_assignment.enabled,
            ),
          },
          numbering: {
            normal: coerceNumberingType(
              po?.numbering?.normal,
              defaultsPo.numbering.normal,
            ),
            additive: coerceNumberingType(
              po?.numbering?.additive,
              defaultsPo.numbering.additive,
            ),
            deductive: coerceNumberingType(
              po?.numbering?.deductive,
              defaultsPo.numbering.deductive,
            ),
          },
        },
        spend_projects: {
          ...defaultsSp,
          ...(sp ?? {}),
          exchange_rate: {
            default: coerceExchangeRate(
              sp?.exchange_rate?.default,
              defaultsSp.exchange_rate.default,
            ),
            visible: coerceBool(
              sp?.exchange_rate?.visible,
              defaultsSp.exchange_rate.visible,
            ),
          },
          supplier_type_keys: Array.isArray(sp?.supplier_type_keys)
            ? sp.supplier_type_keys.filter(
                (key): key is string => typeof key === 'string' && key !== '',
              )
            : defaultsSp.supplier_type_keys,
        },
        gym: {
          ...defaultsGym,
          ...(gym ?? {}),
          daily_close: {
            enabled: coerceBool(
              gym?.daily_close?.enabled,
              defaultsGym.daily_close.enabled,
            ),
            emails: coerceEmailList(
              gym?.daily_close?.emails,
              defaultsGym.daily_close.emails,
            ),
          },
        },
        pos: {
          require_open_shift: coerceBool(
            fromAuth.modules?.pos?.require_open_shift,
            true,
          ),
          blind_shift_close: coerceBool(
            fromAuth.modules?.pos?.blind_shift_close,
            false,
          ),
          require_supervisor_on_shift_close: coerceBool(
            fromAuth.modules?.pos?.require_supervisor_on_shift_close,
            false,
          ),
          shift_close_variance_threshold: (() => {
            const n = Number(
              fromAuth.modules?.pos?.shift_close_variance_threshold,
            )
            return Number.isFinite(n) && n >= 0 ? n : 0
          })(),
          max_shift_hours: (() => {
            const raw = fromAuth.modules?.pos?.max_shift_hours as
              | number
              | null
              | undefined
              | string
            if (raw === null || raw === '') return null
            if (raw === undefined) return 12
            const n = Number(raw)
            return Number.isFinite(n) && n >= 0.25 ? n : 12
          })(),
          shift_warning_hours: (() => {
            const n = Number(fromAuth.modules?.pos?.shift_warning_hours)
            return Number.isFinite(n) && n >= 0 ? n : 10
          })(),
          block_sales_when_shift_overdue: coerceBool(
            fromAuth.modules?.pos?.block_sales_when_shift_overdue,
            true,
          ),
          allow_shift_extension: coerceBool(
            fromAuth.modules?.pos?.allow_shift_extension,
            true,
          ),
          shift_extension_hours: (() => {
            const n = Number(fromAuth.modules?.pos?.shift_extension_hours)
            return Number.isFinite(n) && n >= 0.25 ? n : 4
          })(),
          receipt_footer: String(fromAuth.modules?.pos?.receipt_footer ?? ''),
          receipt_width_mm:
            Number(fromAuth.modules?.pos?.receipt_width_mm) === 58 ? 58 : 80,
          numbering: {
            mode: 'automatic' as const,
            prefix: coercePrefix(
              fromAuth.modules?.pos?.numbering?.prefix,
              'TK',
            ),
          },
          cash_denominations: {
            bills: normalizeDenominationList(
              fromAuth.modules?.pos?.cash_denominations?.bills,
              [1000, 500, 200, 100, 50, 20],
            ),
            coins: normalizeDenominationList(
              fromAuth.modules?.pos?.cash_denominations?.coins,
              [20, 10, 5, 2, 1, 0.5],
            ),
          },
        },
      },
    }
  })

  const logoUrl = computed(() => auth.user?.tenant?.logo_url ?? null)

  const purchaseOrdersSettings = computed(
    () => settings.value.modules!.purchase_orders!,
  )

  const spendProjectsSettings = computed(
    () => settings.value.modules!.spend_projects!,
  )

  const posSettings = computed(
    () =>
      settings.value.modules!.pos ?? {
        require_open_shift: true,
        blind_shift_close: false,
        require_supervisor_on_shift_close: false,
        shift_close_variance_threshold: 0,
        max_shift_hours: 12,
        shift_warning_hours: 10,
        block_sales_when_shift_overdue: true,
        allow_shift_extension: true,
        shift_extension_hours: 4,
        receipt_footer: '',
        receipt_width_mm: 80 as const,
        numbering: { mode: 'automatic' as const, prefix: 'TK' },
        cash_denominations: {
          bills: [1000, 500, 200, 100, 50, 20],
          coins: [20, 10, 5, 2, 1, 0.5],
        },
      },
  )

  const getPurchaseOrderNumbering = (
    type: keyof TPurchaseOrderNumberingSettings,
  ): TPurchaseOrderNumberingTypeSetting =>
    purchaseOrdersSettings.value.numbering[type] ?? defaultNumbering[type]

  return {
    settings,
    logoUrl,
    timezone: computed(() => settings.value.timezone),
    dateFormat: computed(() => settings.value.date_format),
    timeFormat: computed(() => settings.value.time_format),
    currencySettings: computed(() => settings.value.currency),
    purchaseOrdersSettings,
    spendProjectsSettings,
    posSettings,
    getPurchaseOrderNumbering,
  }
}
